// @flow

import * as React from 'react';

import classNames from '@ta/common.classnames';
import { featureIsEnabled } from '@ta/common.features';
import { unescaped } from '@ta/common.i18n';
import Logging from '@ta/common.logging';
import { Link } from '@ta/common.routing';
import { AccommodationOffers } from '@ta/social.offers';
import { NoPhotoAvailableByPlaceType } from '@ta/social.place-type';
import { LocationSectionPhotoWrap } from '@ta/social.location-section';
import { BubbleRatingWithReviewCount } from '@ta/styleguide.bubble-rating';
import { InteractionLogger } from '@ta/tracking.interactions';
import { BookmarkIcon } from '@ta/trips.bookmark-icon';
import { QueriedTripFlowSelector } from '@ta/trips.trip-flow-selector';

import styles from './ShelfLocationSection.less';

const buildLocationUrlForCard = (location: any) => {
  if (location && location.url) {
    Logging.warn('ShelfLocationSection using location.url instead of location.route.url!');
  }
  return location?.route?.url || location?.url;
};

type Props = {
  /** The json location object */
  location: any,
  /** The optional title text to show below the location information */
  title?: string,
  /** The default snippet for the location if it exists */
  defaultSnippet?: any,
  /** */
  comparisonRate?: number,
};

const ESCAPE_MAP = {
  '\u0086': `<span class="${styles.hit}">`,
  '\u0087': '</span>',
  '\n': '<br>',
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

const reviewTextEscape = (text: ?string) =>
  (text || '').replace(/[&<>"'\n\u0086\u0087]|&#?[0-9a-zA-Z]+;/g, s => ESCAPE_MAP[s] || s);

const ShelfLocationSection = (props: Props) => {
  const { location, title, defaultSnippet, comparisonRate } = props;
  const hasPhotos = location.thumbnail;
  const locationUrl = buildLocationUrlForCard(location);

  if (location.isGeo) {
    return (
      <InteractionLogger>
        {logFeedClick => (
          <div className={`ui_shelf_item_container ui_geo_shelf_item ${styles.section} `}>
            <Link
              href={locationUrl}
              onClick={e => logFeedClick(e, 'feedClick')}
              className="ui_poi_thumbnail"
            >
              {hasPhotos ? (
                <LocationSectionPhotoWrap
                  location={location}
                  geoShelfPhotoClassName={styles.shelfForPhoto}
                />
              ) : (
                <span className={styles.shelfForPhoto}>
                  <NoPhotoAvailableByPlaceType location={location} />
                </span>
              )}
            </Link>
            <Link
              href={locationUrl}
              className="item poi_name ui_link"
              onClick={e => logFeedClick(e, 'feedClick')}
            >
              <span className={styles.name}>{location.name}</span>
            </Link>
          </div>
        )}
      </InteractionLogger>
    );
  }
  return (
    <InteractionLogger>
      {logInteraction => (
        <div className={styles.section}>
          <div
            className={classNames(styles.save_to_trip_cta, {
              [styles.heart]: featureIsEnabled('trips_saves_heart'),
            })}
          >
            <QueriedTripFlowSelector object={location}>
              {(openFlow, active) => (
                <BookmarkIcon
                  active={active}
                  bookmarkStyle="stacked"
                  onClick={e => {
                    logInteraction(e, 'save', { locationId: location.id });
                    openFlow();
                  }}
                />
              )}
            </QueriedTripFlowSelector>
          </div>
          <Link
            href={locationUrl}
            className="ui_poi_thumbnail"
            onClick={e => logInteraction(e, 'feedClick')}
          >
            {hasPhotos ? (
              <LocationSectionPhotoWrap
                location={location}
                geoShelfPhotoClassName={styles.shelfForPhoto}
              />
            ) : (
              <span className={styles.shelfForPhoto}>
                {' '}
                <NoPhotoAvailableByPlaceType location={location} />
              </span>
            )}
          </Link>
          <Link
            href={locationUrl}
            className="item poi_name ui_link"
            onClick={e => logInteraction(e, 'feedClick')}
          >
            <span className={styles.name}>{location.name}</span>
          </Link>
          {location.reviewSummary &&
            location.reviewSummary.count !== 0 &&
            location.placeType !== 'NEIGHBORHOOD_TIER_1' && (
              <Link href={locationUrl} onClick={e => logInteraction(e, 'feedClick')}>
                <span className="ui_shelf_poi_bubble_rating">
                  <BubbleRatingWithReviewCount
                    numReviews={location.reviewSummary.count}
                    rating={location.reviewSummary.rating}
                    fontSize={12}
                  />
                </span>
              </Link>
            )}
          {/* Accommodation Offers: Display price when available for a given location */}
          {location.placeType === 'ACCOMMODATION' && comparisonRate ? (
            <AccommodationOffers offer={comparisonRate} locationUrl={locationUrl} />
          ) : (
            <>
              {title && <div className={styles.subheader}>{unescaped(title)}</div>}
              {defaultSnippet && (
                <Link
                  className={`${styles.snippet} ui_link`}
                  href={defaultSnippet.url}
                  onClick={e => logInteraction(e, 'linkedSnippetClick')}
                >
                  &ldquo;{unescaped(reviewTextEscape(defaultSnippet.text))}&rdquo;
                </Link>
              )}
            </>
          )}
        </div>
      )}
    </InteractionLogger>
  );
};

ShelfLocationSection.defaultProps = {
  title: undefined,
  defaultSnippet: undefined,
};

export default ShelfLocationSection;
